import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader, Save, Upload, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useLernsets } from '../LearnsetContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';
import { useCanGenerate, useRemainingGenerations } from '../../abo/useLimits';
import {
    FREE_MAX_CARDS_PER_GEN,
    PRO_MAX_CARDS_PER_GEN,
    FREE_MAX_TOPIC_LENGTH,
    PRO_MAX_TOPIC_LENGTH,
    FREE_MAX_FILE_SIZE_MB,
    PRO_MAX_FILE_SIZE_MB,
    STRIPE_ENABLED,
} from '../../util/constants';

interface GeneratedCard {
    question: string;
    answer: string;
}

const AI_Generator = () => {
    const navigate = useNavigate();
    const { profile, profileLoading, refreshProfile } = useAuth();
    const { lernsets, addLernset, updateLernset } = useLernsets();

    const canGenerate = useCanGenerate();
    const remaining = useRemainingGenerations();

    const maxCards = profile?.tier === 'pro' ? PRO_MAX_CARDS_PER_GEN : FREE_MAX_CARDS_PER_GEN;
    const maxTopicLength = profile?.tier === 'pro' ? PRO_MAX_TOPIC_LENGTH : FREE_MAX_TOPIC_LENGTH;
    const maxFileSizeMB = profile?.tier === 'pro' ? PRO_MAX_FILE_SIZE_MB : FREE_MAX_FILE_SIZE_MB;

    const [activeTab, setActiveTab] = useState<'ai_service' | 'upload'>('ai_service');
    const [topic, setTopic] = useState('');
    const [cardCount, setCardCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [generatedCards, setGeneratedCards] = useState<GeneratedCard[]>([]);
    const [generationError, setGenerationError] = useState<string | null>(null);
    const [generationSuccess, setGenerationSuccess] = useState(false);

    const [file, setFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [accuracy, setAccuracy] = useState<'low' | 'medium' | 'high'>('medium');
    const [additionalInstructions, setAdditionalInstructions] = useState('');

    const [targetOption, setTargetOption] = useState<'new' | 'existing'>('new');
    const [selectedLernsetId, setSelectedLernsetId] = useState<string>('');
    const [newLernsetName, setNewLernsetName] = useState('');
    const [newLernsetDesc, setNewLernsetDesc] = useState('');
    const [replaceCards, setReplaceCards] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const generateFlashcards = httpsCallable(functions, 'generateFlashcards');
    const generateFlashcardsFromDocument = httpsCallable(functions, 'generateFlashcardsFromDocument');

    const onDrop = (acceptedFiles: File[]) => {
        setFileError(null);
        if (!acceptedFiles || acceptedFiles.length === 0) {
            setFileError('Bitte wähle eine unterstützte Datei aus (PDF, DOCX, PPTX oder TXT).');
            return;
        }
        const file = acceptedFiles[0];
        if (file.size > maxFileSizeMB * 1024 * 1024) {
            setFileError(`Die Datei ist zu groß. Maximal ${maxFileSizeMB} MB erlaubt. (Aktuell: ${(file.size / 1024 / 1024).toFixed(1)} MB)`);
            return;
        }
        setFile(file);
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
            'application/zip': ['.pptx'],
            'text/plain': ['.txt'],
        },
        multiple: false,
    });

    const handleGenerate = async () => {
        if (!topic.trim()) {
            setGenerationError('Bitte gib ein Thema oder eine Beschreibung ein.');
            return;
        }

        if (!canGenerate) {
            setGenerationError(`Dein tägliches Limit ist erreicht (${remaining} von 3 übrig). Bitte warte bis morgen oder upgrade auf Pro.`);
            return;
        }

        setLoading(true);
        setGenerationError(null);
        setGenerationSuccess(false);
        setGeneratedCards([]);

        try {
            const result = await generateFlashcards({ topic: topic.trim(), count: cardCount });
            const data = result.data as { cards: GeneratedCard[] };

            if (!data.cards || data.cards.length === 0) {
                setGenerationError('Es wurden keine Karteikarten generiert. Bitte versuche es mit einem anderen Thema.');
                setLoading(false);
                return;
            }

            setGeneratedCards(data.cards);
            setGenerationSuccess(true);
            await refreshProfile();
        } catch (error: any) {
            console.error('Generierungsfehler:', error);
            const rawMessage = error.message || error.details || 'Unbekannter Fehler';

            // Nutzerfreundliche Fehlermeldungen
            let userMessage = 'Bei der Generierung ist ein Fehler aufgetreten. ';
            if (rawMessage.includes('Zeichen') || rawMessage.includes('Zeichen')) {
                userMessage = `Der eingegebene Text ist zu lang. Maximal ${maxTopicLength} Zeichen erlaubt. (Aktuell: ${topic.length} Zeichen)`;
            } else if (rawMessage.includes('MB') || rawMessage.includes('MB')) {
                userMessage = `Die Datei ist zu groß. Maximal ${maxFileSizeMB} MB erlaubt.`;
            } else if (rawMessage.includes('Karteikarten') || rawMessage.includes('Karten')) {
                userMessage = `Maximal ${maxCards} Karteikarten pro Generierung erlaubt.`;
            } else if (rawMessage.includes('tägliches Limit') || rawMessage.includes('Limit')) {
                userMessage = `Dein tägliches Limit ist erreicht (${remaining} von 3 übrig). Bitte warte bis morgen oder upgrade auf Pro.`;
            } else if (rawMessage.includes('Invalid')) {
                userMessage = 'Die Anfrage war ungültig. Bitte überprüfe deine Eingaben und versuche es erneut.';
            } else {
                userMessage += `Fehler: ${rawMessage}`;
            }
            setGenerationError(userMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleUploadGenerate = async () => {
        if (!file) {
            setGenerationError('Bitte wähle zuerst eine Datei aus.');
            return;
        }

        if (!canGenerate) {
            setGenerationError(`Dein tägliches Limit ist erreicht (${remaining} von 3 übrig). Bitte warte bis morgen oder upgrade auf Pro.`);
            return;
        }

        setLoading(true);
        setGenerationError(null);
        setGenerationSuccess(false);
        setGeneratedCards([]);

        try {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async () => {
                try {
                    const base64 = (reader.result as string).split(',')[1];
                    const fileType = file.type;

                    const result = await generateFlashcardsFromDocument({
                        fileContent: base64,
                        fileType,
                        accuracy,
                        additionalInstructions: additionalInstructions.trim() || undefined,
                    });

                    const data = result.data as { cards: GeneratedCard[] };

                    if (!data.cards || data.cards.length === 0) {
                        setGenerationError('Es wurden keine Karteikarten generiert. Bitte versuche es mit einer anderen Datei oder Genauigkeit.');
                        setLoading(false);
                        return;
                    }

                    setGeneratedCards(data.cards);
                    setGenerationSuccess(true);
                    await refreshProfile();
                    setLoading(false);
                } catch (err: any) {
                    console.error('Generierungsfehler:', err);
                    const rawMessage = err.message || err.details || 'Unbekannter Fehler';
                    let userMessage = 'Bei der Generierung ist ein Fehler aufgetreten. ';
                    if (rawMessage.includes('Zeichen') || rawMessage.includes('Zeichen')) {
                        userMessage = `Der extrahierte Text ist zu lang. Maximal ${maxTopicLength} Zeichen erlaubt. Bitte verwende eine kleinere Datei oder upgrade auf Pro für mehr Zeichen.`;
                    } else if (rawMessage.includes('MB') || rawMessage.includes('MB')) {
                        userMessage = `Die Datei ist zu groß. Maximal ${maxFileSizeMB} MB erlaubt. (Aktuell: ${(file.size / 1024 / 1024).toFixed(1)} MB)`;
                    } else if (rawMessage.includes('Karteikarten') || rawMessage.includes('Karten')) {
                        userMessage = `Maximal ${maxCards} Karteikarten pro Generierung erlaubt.`;
                    } else if (rawMessage.includes('tägliches Limit') || rawMessage.includes('Limit')) {
                        userMessage = `Dein tägliches Limit ist erreicht. Bitte warte bis morgen oder upgrade auf Pro.`;
                    } else if (rawMessage.includes('File') || rawMessage.includes('Datei')) {
                        userMessage = 'Die Datei konnte nicht gelesen werden. Bitte stelle sicher, dass es sich um ein unterstütztes Format handelt (PDF, DOCX, PPTX, TXT).';
                    } else {
                        userMessage += `Fehler: ${rawMessage}`;
                    }
                    setGenerationError(userMessage);
                    setLoading(false);
                }
            };
            reader.onerror = () => {
                setGenerationError('Fehler beim Lesen der Datei. Bitte versuche es erneut.');
                setLoading(false);
            };
        } catch (error: any) {
            console.error('Generierungsfehler:', error);
            setGenerationError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es später erneut.');
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (generatedCards.length === 0) {
            setSaveError('Es wurden noch keine Karteikarten generiert.');
            return;
        }

        setSaving(true);
        setSaveError(null);

        try {
            let targetLernsetId = selectedLernsetId;

            // Neues Lernset erstellen
            if (targetOption === 'new') {
                if (!newLernsetName.trim()) {
                    setSaveError('Bitte gib einen Namen für das neue Lernset ein.');
                    setSaving(false);
                    return;
                }
                // 🔥 ID direkt vom Rückgabewert nehmen – NICHT aus dem State!
                targetLernsetId = await addLernset(newLernsetName.trim(), newLernsetDesc.trim());
                // Kurze Pause, damit Firestore die Daten schreiben kann
                await new Promise(resolve => setTimeout(resolve, 200));
            }

            if (!targetLernsetId) {
                setSaveError('Kein Lernset ausgewählt oder erstellt.');
                setSaving(false);
                return;
            }

            // Karten zum Speichern vorbereiten
            const now = Date.now();
            const cardsToSave = generatedCards.map((c, idx) => ({
                id: `${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 9)}`,
                question: c.question,
                answer: c.answer,
                strength: 50,
                nextDueAt: now,
                lastReviewedAt: now,
                reviewCount: 0,
                createdAt: now,
            }));

            // Prüfen ob das Lernset im State ist (für bestehende Lernsets)
            const currentLernset = lernsets.find(ls => ls.id === targetLernsetId);

            if (!currentLernset && targetOption === 'existing') {
                // Bei bestehendem Lernset sollte es im State sein
                setSaveError('Das Lernset konnte nicht gefunden werden. Bitte versuche es erneut.');
                setSaving(false);
                return;
            }

            if (targetOption === 'new') {
                // Neues Lernset: direkt die Karten setzen (keine vorhandenen Karten)
                await updateLernset(targetLernsetId, { cards: cardsToSave });
            } else {
                // Bestehendes Lernset: anhängen oder ersetzen
                let updatedCards;
                if (replaceCards) {
                    updatedCards = cardsToSave;
                } else {
                    updatedCards = [...currentLernset!.cards, ...cardsToSave];
                }
                await updateLernset(targetLernsetId, { cards: updatedCards });
            }

            navigate(`/lernset/${targetLernsetId}`);
        } catch (error: any) {
            console.error('Fehler beim Speichern:', error);
            setSaveError('Speichern fehlgeschlagen: ' + (error.message || 'Unbekannter Fehler'));
        } finally {
            setSaving(false);
        }
    };

    if (profileLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24 flex items-center justify-center">
                <Loader className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24">
            <div className="max-w-4xl mx-auto">
                <Link to="/lernsets" className="inline-flex items-center text-gray-600 hover:text-indigo-600 mb-6 transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Zurück zu meinen Lernsets
                </Link>

                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Neue Karteikarten erstellen</h1>
                <p className="text-gray-600 mb-4">
                    {profile?.tier === 'pro'
                        ? `Pro-Konto: Du hast heute noch ${remaining} von 100 Generierungen übrig.`
                        : `Free-Konto: Du hast heute noch ${remaining} von 3 Generierungen übrig.`
                    }
                </p>

                {!canGenerate && (
                    <div className="bg-yellow-50 text-yellow-800 p-4 rounded-xl mb-6 border border-yellow-200 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div>
                            <span className="font-semibold">Keine Generierungen mehr übrig.</span>
                            {STRIPE_ENABLED ? (
                                <Link to="/upgrade" className="font-semibold underline hover:text-yellow-900 ml-1">
                                    Upgrade auf Pro
                                </Link>
                            ) : (
                                <span> Bitte versuche es morgen wieder.</span>
                            )}
                            {STRIPE_ENABLED && ' für unbegrenzte Generierungen.'}
                        </div>
                    </div>
                )}

                <div className="flex border-b border-gray-200 mb-6">
                    <button
                        onClick={() => {
                            setActiveTab('ai_service');
                            setGenerationError(null);
                            setGenerationSuccess(false);
                            setFileError(null);
                        }}
                        className={`flex-1 py-3 px-4 text-center font-medium transition-colors relative ${
                            activeTab === 'ai_service'
                                ? 'text-indigo-600 border-b-2 border-indigo-600'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Sparkles className="w-5 h-5 inline mr-2" />
                        KI-generieren (Thema eingeben)
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('upload');
                            setGenerationError(null);
                            setGenerationSuccess(false);
                            setFileError(null);
                        }}
                        className={`flex-1 py-3 px-4 text-center font-medium transition-colors relative ${
                            activeTab === 'upload'
                                ? 'text-indigo-600 border-b-2 border-indigo-600'
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <Upload className="w-5 h-5 inline mr-2" />
                        Skript hochladen
                    </button>
                </div>

                {/* ⚠️ Fehler- und Erfolgsmeldungen */}
                <AnimatePresence>
                    {generationError && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6 flex items-start gap-3"
                        >
                            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{generationError}</span>
                        </motion.div>
                    )}

                    {generationSuccess && !generationError && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-xl mb-6 flex items-start gap-3"
                        >
                            <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">✅ {generatedCards.length} Karteikarten wurden erfolgreich generiert!</span>
                        </motion.div>
                    )}

                    {saveError && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6 flex items-start gap-3"
                        >
                            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{saveError}</span>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence mode="wait">
                    {activeTab === 'ai_service' && (
                        <motion.div
                            key="ai_service"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/50 p-6 mb-8">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">Thema oder Beschreibung</h2>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Was soll gelernt werden?
                                        </label>
                                        <textarea
                                            value={topic}
                                            onChange={(e) => {
                                                if (e.target.value.length <= maxTopicLength) setTopic(e.target.value);
                                            }}
                                            rows={4}
                                            className={`w-full px-4 py-3 rounded-xl border ${topic.length > maxTopicLength ? 'border-red-300' : 'border-gray-200'} bg-white/50 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none resize-none`}
                                            placeholder="z.B. 'Die französische Revolution', 'Grundlagen der Elektrotechnik', 'Python-Programmierung für Anfänger' …"
                                        />
                                        <div className={`text-xs text-right mt-1 ${topic.length > maxTopicLength ? 'text-red-500' : 'text-gray-400'}`}>
                                            {topic.length}/{maxTopicLength} Zeichen
                                            {topic.length > maxTopicLength && ' ⚠️ Zu lang!'}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <div className="w-32">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Anzahl Karten
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                max={maxCards}
                                                value={cardCount}
                                                onChange={(e) => setCardCount(Math.min(maxCards, parseInt(e.target.value) || 1))}
                                                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/50 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                                            />
                                        </div>
                                        <button
                                            onClick={handleGenerate}
                                            disabled={loading || !topic.trim() || !canGenerate || topic.length > maxTopicLength}
                                            className="mt-6 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader className="w-5 h-5 animate-spin" />
                                                    Generiere...
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles className="w-5 h-5" />
                                                    Karten generieren
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'upload' && (
                        <motion.div
                            key="upload"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/50 p-6 mb-8">
                                <h2 className="text-xl font-semibold text-gray-800 mb-4">Lernstoff hochladen</h2>
                                <div className="space-y-4">
                                    <div
                                        {...getRootProps()}
                                        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors bg-white/50 ${
                                            fileError ? 'border-red-400 hover:border-red-500' : 'border-gray-300 hover:border-indigo-400'
                                        }`}
                                    >
                                        <input {...getInputProps()} />
                                        {file ? (
                                            <div>
                                                <p className="text-indigo-600 font-medium">{file.name}</p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                                    {file.size > maxFileSizeMB * 1024 * 1024 && ' ⚠️ Zu groß!'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div>
                                                <p className="text-gray-500">
                                                    PDF, DOCX, PPTX oder TXT hier ablegen oder klicken
                                                </p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Max. {maxFileSizeMB} MB (Free) / 10 MB (Pro)
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    {fileError && (
                                        <p className="text-red-600 text-sm flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4" />
                                            {fileError}
                                        </p>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Genauigkeit</label>
                                        <div className="flex gap-2">
                                            {(['low', 'medium', 'high'] as const).map((level) => (
                                                <button
                                                    key={level}
                                                    type="button"
                                                    onClick={() => setAccuracy(level)}
                                                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                                                        accuracy === level
                                                            ? 'bg-indigo-600 text-white shadow-md'
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {level === 'low' ? 'Niedrig' : level === 'medium' ? 'Mittel' : 'Hoch'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Zusätzliche Anweisungen (optional)
                                        </label>
                                        <textarea
                                            value={additionalInstructions}
                                            onChange={(e) => setAdditionalInstructions(e.target.value)}
                                            rows={3}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none resize-none"
                                            placeholder="z.B. 'Konzentriere dich auf die wichtigsten Formeln' …"
                                        />
                                    </div>

                                    <div className="flex justify-end">
                                        <button
                                            onClick={handleUploadGenerate}
                                            disabled={loading || !file || !canGenerate || !!fileError}
                                            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader className="w-5 h-5 animate-spin" />
                                                    Generiere...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload className="w-5 h-5" />
                                                    Karten generieren
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {generatedCards.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/50 p-6 mb-8"
                    >
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">
                            Generierte Karteikarten ({generatedCards.length})
                        </h2>
                        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                            {generatedCards.map((card, idx) => (
                                <div key={idx} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                    <p className="text-sm font-medium text-gray-500 mb-1">Frage {idx + 1}</p>
                                    <p className="text-gray-800 mb-2">{card.question}</p>
                                    <p className="text-sm font-medium text-gray-500 mb-1">Antwort</p>
                                    <p className="text-gray-800">{card.answer}</p>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-gray-200 mt-6 pt-6">
                            <h3 className="font-semibold text-gray-800 mb-4">In welchem Lernset möchtest du die Karten speichern?</h3>
                            <div className="space-y-4">
                                <label className="flex items-start gap-3 p-4 bg-white/50 rounded-xl border border-gray-200 cursor-pointer hover:bg-white/80 transition-colors">
                                    <input
                                        type="radio"
                                        name="target"
                                        checked={targetOption === 'new'}
                                        onChange={() => setTargetOption('new')}
                                        className="mt-1"
                                    />
                                    <div className="flex-1">
                                        <span className="font-medium text-gray-800">Neues Lernset</span>
                                        <div className="mt-2 space-y-2">
                                            <input
                                                type="text"
                                                placeholder="Name des neuen Lernsets *"
                                                value={newLernsetName}
                                                onChange={(e) => setNewLernsetName(e.target.value)}
                                                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                                                disabled={targetOption !== 'new'}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Beschreibung (optional)"
                                                value={newLernsetDesc}
                                                onChange={(e) => setNewLernsetDesc(e.target.value)}
                                                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                                                disabled={targetOption !== 'new'}
                                            />
                                        </div>
                                    </div>
                                </label>

                                <label className="flex items-start gap-3 p-4 bg-white/50 rounded-xl border border-gray-200 cursor-pointer hover:bg-white/80 transition-colors">
                                    <input
                                        type="radio"
                                        name="target"
                                        checked={targetOption === 'existing'}
                                        onChange={() => setTargetOption('existing')}
                                        className="mt-1"
                                    />
                                    <div className="flex-1">
                                        <span className="font-medium text-gray-800">Bestehendes Lernset</span>
                                        <div className="mt-2">
                                            <select
                                                value={selectedLernsetId}
                                                onChange={(e) => setSelectedLernsetId(e.target.value)}
                                                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white/80 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                                                disabled={targetOption !== 'existing'}
                                            >
                                                <option value="">-- Lernset auswählen --</option>
                                                {lernsets.map(ls => (
                                                    <option key={ls.id} value={ls.id}>
                                                        {ls.name} ({ls.cards.length} Karten)
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {targetOption === 'existing' && selectedLernsetId && (
                                            <div className="mt-3 flex items-center gap-4">
                                                <label className="flex items-center gap-2 text-sm text-gray-600">
                                                    <input
                                                        type="checkbox"
                                                        checked={replaceCards}
                                                        onChange={(e) => setReplaceCards(e.target.checked)}
                                                    />
                                                    Vorhandene Karten ersetzen
                                                </label>
                                                <span className="text-sm text-gray-400">(sonst werden sie angehängt)</span>
                                            </div>
                                        )}
                                    </div>
                                </label>
                            </div>

                            <button
                                onClick={handleSave}
                                disabled={
                                    saving ||
                                    (targetOption === 'new' && !newLernsetName.trim()) ||
                                    (targetOption === 'existing' && !selectedLernsetId)
                                }
                                className="mt-6 w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium shadow-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? (
                                    <>
                                        <Loader className="w-5 h-5 animate-spin" />
                                        Speichere...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" />
                                        Karten speichern
                                    </>
                                )}
                            </button>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default AI_Generator;